import React, { useState, useEffect } from "react";
import {
  Button,
  Col,
  Container,
  Form,
  Row,
  Placeholder,
} from "react-bootstrap";
import { FormGroup, Label, Input } from "reactstrap";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { catchAsync, checkResponse } from "../../../../utilities/utilities";
import {
  GET_HOTELS_LIST_FOR_FULL_PANEL_HANDLE_API,
  HOTELLIST,
  ROOMADD,
  ROOMESSENTAILLIST,
  ROOMLIST,
  ROOMTYPE,
  ROOMTYPELIST,
  ROOMUPDATE,
} from "../../../../services/ApiCalls";
import Loader from "../../../../components/Common/Loader";
import { useTranslation } from "react-i18next";
import multipleFileUploader from "../../../../utilities/multipleFileUploader";
import ConfirmationPop from "../../../../components/Modals/ConfirmationPop";

const unavailableDateFormat = "YYYY-MM-DD";

const optionalNumberSchema = (label) =>
  z.preprocess(
    (value) =>
      value === "" ||
      value === null ||
      value === undefined ||
      Number.isNaN(value)
        ? undefined
        : Number(value),
    z
      .number({ invalid_type_error: `${label} must be a number` })
      .min(0, `${label} cannot be negative`)
      .optional()
  );

const optionalRatingSchema = z.preprocess(
  (value) =>
    value === "" ||
    value === null ||
    value === undefined ||
    Number.isNaN(value)
      ? undefined
      : Number(value),
  z
    .number({ invalid_type_error: "Average rating must be a number" })
    .min(0, "Average rating cannot be negative")
    .max(5, "Average rating must be 5 or less")
    .optional()
);

const unavailableDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Unavailable date must be YYYY-MM-DD");

const timeSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Time must be HH:mm");

const pricingDetailSchema = z
  .object({
    _id: z.string().optional(),
    checkInTime: z.object({
      from: timeSchema,
      to: timeSchema,
    }),
    pricingType: z.enum(["hour", "day"], {
      required_error: "Pricing type is required",
    }),
    duration: z.coerce
      .number()
      .int("Duration must be an integer")
      .min(1, "Duration is required"),
    weekdayPrice: z.coerce.number().min(1, "Weekday price is required"),
    weekendPrice: optionalNumberSchema("Weekend price"),
    isActive: z.boolean(),
  })
  .superRefine((price, ctx) => {
    if (price.pricingType === "day" && price.duration !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["duration"],
        message: "Day pricing duration must be 1",
      });
    }
  });

const roomSchema = z.object({
  hotelId: z.string().nonempty("Hotel is required"),
  // checkInHour: z.string().min(1, "Check-in hour is required"),
  // checkOutHour: z.string().min(1, "Check-out hour is required"),
  type: z.string().nonempty("Room type is required"),
  // floorNumber: z.coerce
  //   .number()
  //   .int("Floor number must be an integer")
  //   .min(1, "Floor number must be greater than 0"),

  maxOccupancy: z.coerce
    .number()
    .int("Max occupancy must be an integer")
    .min(1, "Number of people must be greater than 0"),

  length: z.coerce
    .number()
    .int("Length must be an integer")
    .min(1, "Length is required"),

  width: z.coerce
    .number()
    .int("Width must be an integer")
    .min(1, "Width is required"),

  // roomNumber: z
  //   .string()
  //   .min(1, "Room number is required")
  //   .refine((val) => /^\d+$/.test(val) && Number(val) > 0, {
  //     message: "Room number must be a valid number greater than 0",
  //   }),

  // numberOfBed: z
  //   .string()
  //   .nonempty("Number of beds is required")
  //   .regex(/^\d+$/, "Number of beds must be an integer")
  //   .refine((val) => parseInt(val, 10) >= 1, {
  //     message: "Number of beds must be at least 1",
  //   }),
  housekeepingNotes: z.string().optional(),
  roomImages: z
    .array(z.string())
    .min(1, "At least one image is required"),
  amenities: z.array(z.string()).min(1, "Select at least one amenity"),
  status: z.enum(["Available","Unavailable", "Occupied", "Maintenance","Active","Inactive"], {
    required_error: "Status is required",
  }),
  bedType: z
    .string()
    .nonempty("Bed Type is required"),
  noOfRoom: optionalNumberSchema("No. of rooms"),
  totalRoom: optionalNumberSchema("Total rooms"),
  availableRoom: optionalNumberSchema("Available rooms"),
  maxChilOccupancy: optionalNumberSchema("Child occupancy"),
  extraAdultPrice: optionalNumberSchema("Extra adult price"),
  extraChildPrice: optionalNumberSchema("Extra child price"),
  avgRating: optionalRatingSchema,
  unavailableOnDates: z.array(unavailableDateSchema).optional(),
  pricingDetail: z
    .array(pricingDetailSchema)
    .min(1, "Enter at least one pricing detail"),
  // refundPolicy: z.object({
  //   type: z
  //     .enum(["Non-refundable", "Full Refund", "Partial Refund"])
  //     .optional(),
  //   description: z.string().max(500).optional(),
  //   cutoffHours: z.coerce.number().min(1).optional(),
  // }),
  // pricing: z
  //   .array(
  //     z.object({
  //       durationType: z.string().nonempty("Duration type is required"),
  //       basePrice: z.coerce.number().min(0, "Base price must be positive"),
  //       weekRate: z.coerce.number().min(0, "Week rate cannot be negative"),
  //       discount: z
  //         .number()
  //         .min(0, { message: "Discount cannot be negative" })
  //         .max(100, { message: "Discount cannot exceed 100%" }),
  //       activated: z.boolean().optional(),
  //       refundable: z.boolean().optional(),
  //     })
  //   )
  //   .min(1, "At least one pricing entry is required"),
}).superRefine((data, ctx) => {
  if (
    data.totalRoom !== undefined &&
    data.availableRoom !== undefined &&
    data.availableRoom > data.totalRoom
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["availableRoom"],
      message: "Available rooms cannot exceed total rooms",
    });
  }
});

const defaultPricingDetail = [
  {
    checkInTime: {
      from: "09:00",
      to: "18:00",
    },
    pricingType: "hour",
    duration: 1,
    weekdayPrice: 0,
    weekendPrice: "",
    isActive: true,
  },
];

const allowedPricingTypes = ["hour", "day"];

const normalizePricingType = (price = {}) => {
  const pricingType = (price.pricingType || price.durationType || "hour")
    .toString()
    .toLowerCase();

  return allowedPricingTypes.includes(pricingType) ? pricingType : "hour";
};

const optionalPayloadValue = (value) =>
  value === "" || value === null || value === undefined || Number.isNaN(value)
    ? undefined
    : value;

const normalizeCheckInTime = (checkInTime = {}) => ({
  from: checkInTime?.from || "09:00",
  to: checkInTime?.to || "18:00",
});

const normalizeUnavailableDates = (dates = []) =>
  dates
    .map((date) => {
      const rawDate =
        typeof date === "string"
          ? date
          : typeof date?.format === "function"
          ? date.format(unavailableDateFormat)
          : date?.type;
      const parsedDate = dayjs(rawDate);

      return parsedDate.isValid()
        ? parsedDate.format(unavailableDateFormat)
        : null;
    })
    .filter(Boolean);

const getLegacyPrice = (price = {}) =>
  optionalPayloadValue(price.weekdayPrice) ??
  optionalPayloadValue(price.finalPrice) ??
  optionalPayloadValue(price.basePrice) ??
  0;

const buildPricingDetail = (pricingDetail = []) => {
  const groupedPricing = new Map();

  pricingDetail.forEach((price) => {
    const pricingType = normalizePricingType(price);
    const duration = pricingType === "day" ? 1 : Number(price.duration || 1);
    const checkInTime = normalizeCheckInTime(price.checkInTime);
    const key = `${pricingType}-${duration}-${checkInTime.from}-${checkInTime.to}`;
    const current = groupedPricing.get(key) || {
      ...(price._id && { _id: price._id }),
      checkInTime,
      pricingType,
      duration,
      weekdayPrice: undefined,
      weekendPrice: undefined,
      isActive: typeof price.isActive === "boolean" ? price.isActive : false,
    };

    const rowWeekdayPrice = optionalPayloadValue(price.weekdayPrice);
    const rowWeekendPrice = optionalPayloadValue(price.weekendPrice);
    const legacyPrice = getLegacyPrice(price);

    if (rowWeekdayPrice !== undefined) {
      current.weekdayPrice = Number(rowWeekdayPrice);
    } else if (price.pricingLabel === "weekend") {
      current.weekendPrice = Number(legacyPrice);
    } else {
      current.weekdayPrice = Number(legacyPrice);
    }

    if (rowWeekendPrice !== undefined) {
      current.weekendPrice = Number(rowWeekendPrice);
    }

    if (typeof price.isActive === "boolean") {
      current.isActive = price.isActive;
    }

    groupedPricing.set(key, current);
  });

  return Array.from(groupedPricing.values()).map((price) => {
    const weekdayPrice = Number(price.weekdayPrice || 0);
    const weekendPrice = optionalPayloadValue(price.weekendPrice);
    const pricingPayload = {
      ...(price._id && { _id: price._id }),
      checkInTime: normalizeCheckInTime(price.checkInTime),
      pricingType: price.pricingType,
      duration: price.pricingType === "day" ? 1 : Number(price.duration || 1),
      weekdayPrice,
      isActive: price.isActive,
    };

    if (weekendPrice !== undefined) {
      pricingPayload.weekendPrice = Number(weekendPrice);
    }

    return pricingPayload;
  });
};

const buildRoomPayload = (data, id) => {
  const totalRoom = optionalPayloadValue(data.totalRoom);

  const payload = {
    ...(id && { _id: id }),
    hotelId: data.hotelId,
    amenities: data.amenities,
    type: data.type,
    // floorNumber: data.floorNumber,
    // roomNumber: data.roomNumber,
    bedType: data.bedType,
    status: data.status,
    maxOccupancy: data.maxOccupancy,
    length: data.length,
    width: data.width,
    housekeepingNotes: data.housekeepingNotes,
    noOfRoom: optionalPayloadValue(data.noOfRoom) ?? totalRoom,
    totalRoom,
    availableRoom: optionalPayloadValue(data.availableRoom),
    roomImages: data.roomImages?.map((image) => image?._id ?? image),
    images: data.roomImages?.map((image) => image?._id ?? image),
    maxChilOccupancy: optionalPayloadValue(data.maxChilOccupancy),
    extraAdultPrice: optionalPayloadValue(data.extraAdultPrice),
    extraChildPrice: optionalPayloadValue(data.extraChildPrice),
    pricingDetail: buildPricingDetail(data.pricingDetail),
    avgRating: optionalPayloadValue(data.avgRating),
    unavailableOnDates: normalizeUnavailableDates(data.unavailableOnDates),
  };

  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined)
  );
};

const AddRoom = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const page = queryParams.get("page") || 1;
  const navigate = useNavigate();
  const [imagePreviews, setImagePreviews] = useState([]);
  const [imgloader, setImgLoader] = useState(false);
  const [roomAmenities, setRoomAmenities] = useState([]);
  const [hotelAdminData, setHotelData] = useState([]);
  const [roomTypeData, setRoomTypeData] = useState([]);
  const [hotelDetailData, setHotelDetailData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [roomModel, setRoomModel] = useState(false);


  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(roomSchema),
    defaultValues: {
      hotelId: "",
      amenities: [],
      // floorNumber: "",
      // roomNumber: "",
      roomImages: [],
      // numberOfBed: "",
      bedType: "Queen",
      type: "",
      status: "Available",
      // checkInHour: "",
      // checkOutHour: "",
      maxOccupancy: "",
      maxChilOccupancy: "",
      noOfRoom: "",
      totalRoom: "",
      availableRoom: "",
      extraAdultPrice: "",
      extraChildPrice: "",
      avgRating: "",
      length: 10,
      width: 20,
      housekeepingNotes: "",
      unavailableOnDates: [],
      pricingDetail: defaultPricingDetail,
    },
  });
  const selectedHotelId = watch("hotelId");


  const getAmenityData = catchAsync(async () => {
    const [roomRes, hotelRes] = await Promise.all([
      ROOMESSENTAILLIST({ limit: 100, page: 1, status: "Active" }),
      GET_HOTELS_LIST_FOR_FULL_PANEL_HANDLE_API(),


    ]);


    checkResponse({ res: roomRes, setData: setRoomAmenities });
    checkResponse({ res: hotelRes, setData: setHotelData });
    if (!id) {
      setValue("hotelId", localStorage.getItem("hotelId"))
    }
  });

  console.log("dfsdgdgdgege", roomTypeData);

  const getData = catchAsync(async () => {
    setLoader(true);
    const res = await ROOMLIST({ id: id });
    const success = checkResponse({
      res,
      setData: setHotelDetailData,
      setLoader,
    });
    if (success && res?.data?.data) {
      const data = res.data.data;
      console.log(data, "data====<>");
      const roomImages = data.roomImages || data.images || [];
      const pricingDetail = data.pricingDetail?.length
        ? data.pricingDetail
        : data.pricing?.length
        ? data.pricing
        : defaultPricingDetail;

      setImagePreviews(roomImages);
      reset({
        hotelName: data?.hotelId?.hotelName || "",
        hotelId: data.hotelId?._id || "",
        amenities: data.amenities?.map((a) => a._id) || [],
        // floorNumber: data.floorNumber || "",
        // roomNumber: data.roomNumber || "",
        bedType: data.bedType || "Queen",
        // numberOfBed: data.numberOfBed != null ? String(data.numberOfBed) : "",
        type: data?.type?._id || "",
        status: data.status || "Available",
        // checkInHour: data?.checkInHour || "",
        // checkOutHour: data?.checkOutHour || "",
        maxOccupancy: data?.maxOccupancy || "",
        maxChilOccupancy:
          data?.maxChilOccupancy || data?.maxChildOccupancy || "",
        noOfRoom: data?.noOfRoom || "",
        totalRoom: data?.totalRoom || data?.noOfRoom || "",
        availableRoom: data?.availableRoom || "",
        extraAdultPrice: data?.extraAdultPrice || "",
        extraChildPrice: data?.extraChildPrice || "",
        avgRating: data?.avgRating || "",
        length: data?.length || 10,
        width: data?.width || 20,
        housekeepingNotes: data?.housekeepingNotes || "",
        unavailableOnDates: normalizeUnavailableDates(
          data?.unavailableOnDates
        ),
        roomImages: roomImages?.map((ele) => ele?._id || ele) || [],
        pricingDetail: buildPricingDetail(pricingDetail),
        // pricing: data?.pricing?.length
        //   ? data?.pricing
        //   : [
        //     {
        //       durationType: "",
        //       basePrice: 0,
        //       weekRate: 0,
        //       discount: 0,
        //       activated: true,
        //       refundable: true,
        //     },
        //   ],
        // refundPolicy: {
        //   type: data?.refundPolicy?.type || "",
        //   description: data?.refundPolicy?.description || "",
        //   cutoffHours: data?.refundPolicy?.cutoffHours || "",
        // },
      });
    }
  }, setLoader);

  useEffect(() => {
    getAmenityData();
    if (id) {
      getData();
    }
  }, [id]);
  const { fields, append, remove } = useFieldArray({
    control,
    name: "pricingDetail",
  });

  const handleCheckbox = (field, value, checked) => {
    const currentValues = watch("amenities") || [];
    let updated;
    if (checked) {
      updated = [...currentValues, value];
    } else {
      updated = currentValues.filter((id) => id !== value);
    }
    setValue("amenities", updated, { shouldValidate: true });
  };

  const handleImageChange = catchAsync(async (e) => {
    setImgLoader(true);

    const files = Array.from(e.target.files);
    for (let file of files) {
      const res = await multipleFileUploader(file);

      if (Array.isArray(res)) {
        const currentIds = watch("roomImages") || [];
        const newIds = res.map((img) => img._id);
        setValue("roomImages", [...currentIds, ...newIds], {
          shouldValidate: true,
        });
        setImagePreviews((prev) => [...prev, ...res]);
      }
    }

    setImgLoader(false);
  });

  const handleRemoveImage = (index) => {
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
    const currentIds = watch("roomImages") || [];
    const updatedIds = currentIds.filter((_, i) => i !== index);

    setValue("roomImages", updatedIds, { shouldValidate: true });
  };


  const submitHandler = catchAsync(async (data) => {
    console.log("data.roomImages", data.roomImages);
    const body = buildRoomPayload(data, id);

    const res = await (id ? ROOMUPDATE(body) : ROOMADD(body));
    const success = checkResponse({ res, showSuccess: true });
    if (success) navigate(`/room?page=${page}`);
  });

  const handleCancel = () => navigate(`/room?page=${page}`);
  console.log(hotelDetailData, "data values===<>");
  console.log(errors, "eroror");
  const pricingValues = watch("pricingDetail") || [];


  const fetchRoomType = async () => {
    const resp = await ROOMTYPELIST({ limit: 100, page: 1, hotelId: selectedHotelId });
    if (resp?.data?.data?.length <= 0) {
      setRoomModel(true)
    }
    checkResponse({ res: resp, setData: setRoomTypeData });
  }

  useEffect(() => {
    if (selectedHotelId) {
      fetchRoomType();
    }
  }, [selectedHotelId])

  return (
    <>
      <ConfirmationPop
        type={"addRoomType"}
        confirmation={roomModel}
        setConfirmation={() =>
          setRoomModel(false)
        }
        confirmHandler={() =>
          navigate("/roomtype/add")
        }
      />
      {loader && <Loader />}
      <section className="addRoom position-relative py-3">
        <Container>
          <Row>
            <Col lg="12">
              <div className="d-flex align-items-center gap-10">
                <Link
                  to={`/room?page=${page}`}
                  className="border d-flex align-items-center p-2 rounded"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 10 16"
                    fill="none"
                  >
                    <path
                      d="M8.64707 0.473344C8.55514 0.381188 8.44594 0.308072 8.32572 0.258184C8.20549 0.208296 8.07661 0.182617 7.94644 0.182617C7.81628 0.182617 7.68739 0.208296 7.56717 0.258184C7.44694 0.308072 7.33774 0.381188 7.24582 0.473344L0.667065 7.05209C0.593675 7.12533 0.53545 7.21233 0.495723 7.3081C0.455996 7.40387 0.435547 7.50654 0.435547 7.61022C0.435547 7.7139 0.455996 7.81657 0.495723 7.91234C0.53545 8.00811 0.593675 8.0951 0.667065 8.16834L7.24582 14.7471C7.63373 15.135 8.25915 15.135 8.64707 14.7471C9.03498 14.3592 9.03498 13.7338 8.64707 13.3458L2.9154 7.60626L8.65498 1.86668C9.03498 1.48668 9.03498 0.853344 8.64707 0.473344Z"
                      fill="#E8307A"
                      stroke="#E8307A"
                      strokeWidth="0.2"
                    />
                  </svg>
                </Link>
                <h4 className="mb-0 py-3 fw-bold text-dark text-capitalize">
                  {id ? t("manageRoom.editRoom") : t("manageRoom.addRoom")}
                </h4>
              </div>
            </Col>

            <Col lg="12" className="my-2">
              <div>
                <Form onSubmit={handleSubmit(submitHandler)}>
                  <Row className="justify-content-between">
                    <Col lg="6" className="my-2">
                      <label>{t("manageRoom.selectHotel")}</label>
                      <select className="form-control" {...register("hotelId")}>
                        <option value="">-- Select Hotel --</option>
                        {hotelAdminData?.hotelList?.map((admin) => (
                          <option key={admin.id} value={admin.id}>
                            {admin.name}
                          </option>
                        ))}
                      </select>
                      {errors?.hotelId && (
                        <p className="text-danger">{errors.hotelId.message}</p>
                      )}
                    </Col>

                    {/* <Col lg="6" className="my-2">
                      <label>{t("manageRoom.columns.floorNumber")}</label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        {...register("floorNumber", {
                          valueAsNumber: true,
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.floorNumber && (
                        <p className="text-danger">
                          {errors.floorNumber.message}
                        </p>
                      )}
                    </Col> */}

                    {/* <Col lg="6" className="my-2">
                      <label>{t("manageRoom.roomNumber")}</label>
                      <input
                        type="text"
                        className="form-control"
                        {...register("roomNumber", {
                          required: "Room number is required",
                          validate: (value) => {
                            if (!/^\d+$/.test(value)) {
                              return "Room number must be a valid number";
                            }
                            if (Number(value) < 1) {
                              return "Room number must be greater than 0";
                            }
                            return true;
                          },
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.roomNumber && (
                        <p className="text-danger">
                          {errors.roomNumber.message}
                        </p>
                      )}
                    </Col> */}

                    {/* <Col lg="6" className="my-2">
                      <label>No. of Rooms</label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        {...register("noOfRoom", {
                          valueAsNumber: true,
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.noOfRoom && (
                        <p className="text-danger">
                          {errors.noOfRoom.message}
                        </p>
                      )}
                    </Col> */}

                    {/* <Col lg="6" className="my-2">
                      <label>{t("manageRoom.numberOfBed")}</label>
                      <input
                        type="text"
                        className="form-control"
                        min={1}
                        {...register("numberOfBed")}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.numberOfBed && (
                        <p className="text-danger">
                          {errors.numberOfBed.message}
                        </p>
                      )}
                    </Col> */}
                    <Col lg="6" className="my-2">
                      <label>{t("manageRoom.columns.bedType")}</label>

                      <Form.Select {...register("bedType")}>
                        <option value="">Select Bed Type</option>
                        <option value="Queen">Queen</option>
                        <option value="Twin">Twin</option>
                        <option value="King">King</option>
                      </Form.Select>
                      {errors.bedType && (
                        <p className="text-danger">
                          {errors.bedType.message}
                        </p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label> {t("manageRoom.roomType")}
                        <span style={{ color: "red", fontSize: 12, marginLeft: 5 }} >{!watch("hotelId") && t("manageRoom.columns.selectHotel")} </span></label>
                      <select key={JSON.stringify(roomTypeData)} className="form-control" {...register("type")}>
                        <option value="">-- Select Room Type --</option>
                        {roomTypeData?.map((admin) => (
                          <option key={admin._id} value={admin._id}>
                            {admin.name}
                          </option>
                        ))}
                      </select>
                      {errors?.type && (
                        <p className="text-danger">{errors.type.message}</p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>{t("manageRoom.columns.status")}</label>

                      <Form.Select {...register("status")}>
                        <option value="">Select Availability</option>
                        <option value="Available">Available</option>
                         <option value="Unavailable">Unavailable</option>
                        <option value="Occupied">Occupied</option>
                        <option value="Maintenance">Maintenance</option>
                                {/* <option value="Active">Active</option>
<option value="Inactive">Inactive</option> */}


                      </Form.Select>
                    </Col>
                    <Col lg="6" className="my-2">
                      <label>Average Rating</label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        max={5}
                        step="0.1"
                        {...register("avgRating", {
                          valueAsNumber: true,
                          min: 0,
                          max: 5,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.avgRating && (
                        <p className="text-danger">
                          {errors.avgRating.message}
                        </p>
                      )}
                    </Col>
                    <Col lg="6" className="my-2">
                      <label>Total Room</label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        {...register("totalRoom", {
                          valueAsNumber: true,
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.totalRoom && (
                        <p className="text-danger">
                          {errors.totalRoom.message}
                        </p>
                      )}
                    </Col>
                    <Col lg="6" className="my-2">
                      <label>Available Room</label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        {...register("availableRoom", {
                          valueAsNumber: true,
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.availableRoom && (
                        <p className="text-danger">
                          {errors.availableRoom.message}
                        </p>
                      )}
                    </Col>
                    {/* <Col lg="6" className="my-2">
                      <label>
                        {t("manageHotel.checkInHour", "CheckInHour")}
                      </label>
                      <input
                        type="time"
                        className="form-control"
                        {...register("checkInHour")}
                      />
                      {errors?.checkInHour && (
                        <p className="text-danger">
                          {errors.checkInHour.message}
                        </p>
                      )}
                    </Col> */}
                    {/* <Col lg="6" className="my-2">
                      <label>
                        {t("manageHotel.checkOutHour", "CheckOutHour")}
                      </label>
                      <input
                        type="time"
                        className="form-control"
                        {...register("checkOutHour")}
                      />
                      {errors?.checkOutHour && (
                        <p className="text-danger">
                          {errors.checkOutHour.message}
                        </p>
                      )}
                    </Col> */}

                    <Col lg="12" className="my-2">
                      <label>{t("manageRoom.housekeepingNotes")}</label>
                      <textarea
                        rows={3}
                        className="form-control"
                        {...register("housekeepingNotes")}
                      />
                    </Col>
                    <Col lg="12" className="my-2">
                      <label>
                        {t(
                          "manageRoom.unavailableOnDates",
                          "Unavailable On Dates"
                        )}
                      </label>
                      <Controller
                        name="unavailableOnDates"
                        control={control}
                        render={({ field }) => (
                          <DatePicker
                            multiple
                            allowClear
                            size="large"
                            format={unavailableDateFormat}
                            value={(field.value || [])
                              .map((date) => dayjs(date))
                              .filter((date) => date.isValid())}
                            onChange={(dates) =>
                              field.onChange(normalizeUnavailableDates(dates))
                            }
                            onBlur={field.onBlur}
                            style={{ width: "100%" }}
                            placeholder={t(
                              "manageRoom.selectUnavailableDates",
                              "Select unavailable dates"
                            )}
                          />
                        )}
                      />
                      {errors.unavailableOnDates && (
                        <p className="text-danger">
                          {errors.unavailableOnDates.message}
                        </p>
                      )}
                    </Col>
                    <Col lg="6" className="my-2">
                      <label>
                        {t("manageRoom.columns.numberOfPeople", "People Allowed")}
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        {...register("maxOccupancy", {
                          valueAsNumber: true,
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.maxOccupancy && (
                        <p className="text-danger">
                          {errors.maxOccupancy.message}
                        </p>
                      )}
                    </Col>
                    <Col lg="6" className="my-2">
                      <label>Children Allowed</label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        {...register("maxChilOccupancy", {
                          valueAsNumber: true,
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.maxChilOccupancy && (
                        <p className="text-danger">
                          {errors.maxChilOccupancy.message}
                        </p>
                      )}
                    </Col>
                    <Col lg="6" className="my-2">
                      <label>Extra Adult Price</label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        {...register("extraAdultPrice", {
                          valueAsNumber: true,
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.extraAdultPrice && (
                        <p className="text-danger">
                          {errors.extraAdultPrice.message}
                        </p>
                      )}
                    </Col>
                    <Col lg="6" className="my-2">
                      <label>Extra Child Price</label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        {...register("extraChildPrice", {
                          valueAsNumber: true,
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.extraChildPrice && (
                        <p className="text-danger">
                          {errors.extraChildPrice.message}
                        </p>
                      )}
                    </Col>
                    <Col lg="6" className="my-2">
                      <label>
                        {t("manageRoom.columns.roomLength", "Room Length (meter)")}
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        {...register("length", {
                          valueAsNumber: true,
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.length && (
                        <p className="text-danger">{errors.length.message}</p>
                      )}
                    </Col>
                    <Col lg="6" className="my-2">
                      <label>
                        {t("manageRoom.columns.roomWidth", "Room Width (meter)")}
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        {...register("width", {
                          valueAsNumber: true,
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.width && (
                        <p className="text-danger">{errors.width.message}</p>
                      )}
                    </Col>

                    <Col lg="12" className="my-2">
                      <label className="fw-semibold">{t("manageRoom.columns.roomAmenities")}</label>
                      <div
                        className="d-flex flex-wrap gap-3 p-2 border rounded"
                        style={{ maxHeight: "200px", overflowY: "auto" }}
                      >
                        {roomAmenities?.length > 0
                          ? roomAmenities.map((amenity) => (
                            <div
                              key={amenity._id}
                              className="checkboxlebels d-flex align-items-center"
                              style={{ minWidth: "150px" }}
                            >
                              <FormGroup check>
                                <Label check>
                                  <Input
                                    type="checkbox"
                                    className="chekboxCustom"
                                    label={amenity.name}
                                    checked={watch("amenities")?.includes(
                                      amenity._id
                                    )}
                                    onChange={(e) =>
                                      handleCheckbox(
                                        "amenities",
                                        amenity._id,
                                        e.target.checked
                                      )
                                    }
                                  />{" "}
                                  {amenity.name}
                                </Label>
                              </FormGroup>
                            </div>
                          ))
                          : Array.from({ length: 6 }).map((_, idx) => (
                            <Placeholder key={idx} xs={8} bg="secondary" />
                          ))}
                      </div>
                      {errors.amenities && (
                        <p className="text-danger">
                          {errors.amenities.message}
                        </p>
                      )}
                    </Col>
                    {/* <Col lg="6" className="my-3">
                      <label className="fw-bold text-muted ps-2 m-0">
                        Refund Policy
                      </label>
                      <div className="p-3 border rounded bg-white shadow-sm">
                        <Form.Select
                          {...register("refundPolicy.type")}
                          className="mb-3"
                        >
                          <option value="">Select Refund Policy</option>
                          <option value="Non-refundable">Non-refundable</option>
                          <option value="Full Refund">Full Refund</option>
                          <option value="Partial Refund">Partial Refund</option>
                        </Form.Select>
                        {errors?.refundPolicy?.type && (
                          <p className="text-danger small">
                            {errors.refundPolicy.type.message}
                          </p>
                        )}

                        {["Full Refund", "Partial Refund"].includes(
                          watch("refundPolicy.type")
                        ) && (
                            <>
                              <Form.Control
                                type="number"
                                placeholder="Enter cutoff hours"
                                className="mb-3"
                                {...register("refundPolicy.cutoffHours", {
                                  valueAsNumber: true,
                                })}
                              />
                              {errors?.refundPolicy?.cutoffHours && (
                                <p className="text-danger small">
                                  {errors.refundPolicy.cutoffHours.message}
                                </p>
                              )}
                            </>
                          )}

                        <Form.Control
                          as="textarea"
                          rows={3}
                          placeholder="Add description (optional)"
                          className="mb-2"
                          {...register("refundPolicy.description")}
                        />
                        {errors?.refundPolicy?.description && (
                          <p className="text-danger small">
                            {errors.refundPolicy.description.message}
                          </p>
                        )}
                      </div>
                    </Col> */}

                    <Col lg="12" className="my-2">
                      <div
                        className="formWrpper rounded p-3"
                        style={{ background: "#f9f9f9" }}
                      >
                        <label className="fw-bold text-muted ps-2 m-0">
                          {t("roomType.columns.uploadImages")}
                        </label>
                        <div
                          className="border border-2 border-dashed rounded d-flex flex-column align-items-center justify-content-center p-4 bg-light"
                          style={{ cursor: "pointer", minHeight: "140px" }}
                          onClick={() =>
                            document.getElementById("imageUploadInput").click()
                          }
                        >
                          <i className="bi bi-cloud-upload fs-1 text-muted"></i>
                          <p className="m-0 text-muted">
                            {t("roomType.columns.imageLabel")}
                          </p>
                          <input
                            id="imageUploadInput"
                            type="file"
                            accept="image/*"
                            multiple
                            className="d-none"
                            onChange={handleImageChange}
                          />
                        </div>

                        {errors.roomImages && (
                          <p className="text-danger mt-1">
                            {errors.roomImages.message}
                          </p>
                        )}

                        <div className="d-flex flex-wrap gap-2 mt-3">
                          {imgloader ? (
                            <div
                              style={{ height: 120, width: 120 }}
                              className="d-flex align-items-center justify-content-center border rounded"
                            >
                              Loading...
                            </div>
                          ) : (
                            imagePreviews.map((img, index) => (
                              <div
                                key={img._id}
                                style={{ width: 120, height: 120, position: "relative" }}
                                className="border rounded overflow-hidden shadow-sm"
                              >
                                <img
                                  src={img.link}
                                  alt={`preview-${index}`}
                                  className="w-100 h-100 object-fit-cover"
                                />

                                <button
                                  type="button"
                                  className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 rounded-circle"
                                  onClick={() => handleRemoveImage(index)}
                                >
                                  ✕
                                </button>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </Col>

                    <Col lg="12" className="my-2">
                      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                        <label className="fw-semibold m-0">
                          Pricing Detail
                        </label>
                        <Button
                          type="button"
                          variant="primary"
                          size="sm"
                          onClick={() =>
                            append({
                              checkInTime: {
                                from: "09:00",
                                to: "18:00",
                              },
                              pricingType: "hour",
                              duration: 1,
                              weekdayPrice: 0,
                              weekendPrice: "",
                              isActive: true,
                            })
                          }
                        >
                          Add Pricing
                        </Button>
                      </div>

                      {fields.map((field, index) => {
                        const selectedPricingType =
                          pricingValues?.[index]?.pricingType || "hour";
                        const isDayPricing = selectedPricingType === "day";

                        return (
                          <div
                            key={field.id}
                            className="border rounded p-3 mb-3 bg-light"
                          >
                            <Row className="align-items-end">
                              <Col lg="2" md="4" className="my-2">
                                <label>
                                  {t("manageRoom.availableFrom", "Available From")}
                                </label>
                                <input
                                  type="time"
                                  className="form-control"
                                  {...register(
                                    `pricingDetail.${index}.checkInTime.from`
                                  )}
                                />
                                {errors.pricingDetail?.[index]?.checkInTime
                                  ?.from && (
                                    <p className="text-danger">
                                      {
                                        errors.pricingDetail[index].checkInTime
                                          .from.message
                                      }
                                    </p>
                                  )}
                              </Col>

                              <Col lg="2" md="4" className="my-2">
                                <label>
                                  {t("manageRoom.availableTill", "Available Till")}
                                </label>
                                <input
                                  type="time"
                                  className="form-control"
                                  {...register(
                                    `pricingDetail.${index}.checkInTime.to`
                                  )}
                                />
                                {errors.pricingDetail?.[index]?.checkInTime
                                  ?.to && (
                                    <p className="text-danger">
                                      {
                                        errors.pricingDetail[index].checkInTime
                                          .to.message
                                      }
                                    </p>
                                  )}
                              </Col>

                              <Col lg="2" md="4" className="my-2">
                                <label>Pricing Type</label>
                                <Form.Select
                                  {...register(
                                    `pricingDetail.${index}.pricingType`,
                                    {
                                      onChange: (e) => {
                                        if (e.target.value === "day") {
                                          setValue(
                                            `pricingDetail.${index}.duration`,
                                            1,
                                            { shouldValidate: true }
                                          );
                                        }
                                      },
                                    }
                                  )}
                                >
                                  <option value="hour">Hour</option>
                                  <option value="day">Day</option>
                                </Form.Select>
                                {errors.pricingDetail?.[index]
                                  ?.pricingType && (
                                    <p className="text-danger">
                                      {
                                        errors.pricingDetail[index].pricingType
                                          .message
                                      }
                                    </p>
                                  )}
                              </Col>

                              <Col lg="2" md="4" className="my-2">
                                <label>Duration</label>
                                <input
                                  type="number"
                                  className="form-control"
                                  min={1}
                                  {...register(
                                    `pricingDetail.${index}.duration`,
                                    { valueAsNumber: true }
                                  )}
                                  readOnly={isDayPricing}
                                  onKeyDown={(e) =>
                                    e.key === "-" && e.preventDefault()
                                  }
                                />
                                {errors.pricingDetail?.[index]?.duration && (
                                  <p className="text-danger">
                                    {
                                      errors.pricingDetail[index].duration
                                        .message
                                    }
                                  </p>
                                )}
                              </Col>

                              <Col lg="2" md="4" className="my-2">
                                <label>Weekday Price</label>
                                <input
                                  type="number"
                                  className="form-control"
                                  min={0}
                                  {...register(
                                    `pricingDetail.${index}.weekdayPrice`,
                                    { valueAsNumber: true }
                                  )}
                                  onKeyDown={(e) =>
                                    e.key === "-" && e.preventDefault()
                                  }
                                />
                                {errors.pricingDetail?.[index]?.weekdayPrice && (
                                  <p className="text-danger">
                                    {
                                      errors.pricingDetail[index].weekdayPrice
                                        .message
                                    }
                                  </p>
                                )}
                              </Col>

                              <Col lg="2" md="4" className="my-2">
                                <label>Weekend Price</label>
                                <input
                                  type="number"
                                  className="form-control"
                                  min={0}
                                  {...register(
                                    `pricingDetail.${index}.weekendPrice`,
                                    { valueAsNumber: true }
                                  )}
                                  onKeyDown={(e) =>
                                    e.key === "-" && e.preventDefault()
                                  }
                                />
                                {errors.pricingDetail?.[index]?.weekendPrice && (
                                  <p className="text-danger">
                                    {
                                      errors.pricingDetail[index].weekendPrice
                                        .message
                                    }
                                  </p>
                                )}
                              </Col>

                              <Col lg="1" md="4" className="my-2">
                                <Form.Check
                                  type="checkbox"
                                  label="Active"
                                  {...register(
                                    `pricingDetail.${index}.isActive`
                                  )}
                                />
                              </Col>

                              <Col lg="12" className="my-2">
                                <Button
                                  type="button"
                                  variant="danger"
                                  size="sm"
                                  onClick={() => remove(index)}
                                  disabled={fields.length === 1}
                                >
                                  Remove
                                </Button>
                              </Col>
                            </Row>
                          </div>
                        );
                      })}
                      {errors.pricingDetail?.message && (
                        <p className="text-danger">
                          {errors.pricingDetail.message}
                        </p>
                      )}
                    </Col>

                    {/* <Col lg="12" className="my-2">
                      <div
                        className="formWrpper rounded p-3"
                        style={{ background: "#f9f9f9" }}
                      >
                        <span className="fw-bold mb-2 d-block">
                          {t("manageRoom.addPrice")}
                        </span>

                        <Row>
                          <Col lg="12" className="my-2">
                            <Row className="fw-semibold text-muted border-bottom pb-2 mb-2">
                              <Col md={2}>Duration Type</Col>
                              <Col md={2}>Week Rate</Col>
                              <Col md={2}>Weekend Rate</Col>
                              <Col md={2}>Discount</Col>
                              <Col md={3}>Options</Col>
                              <Col md={1}></Col>
                            </Row>

                            {fields.map((field, index) => (
                              <Row
                                key={field.id}
                                className="align-items-end mb-3"
                              > 
                               <Col md={2}>
                                  <input
                                    type="text"
                                    className="form-control"
                                    {...register(
                                      `pricing.${index}.durationType`
                                    )}
                                    placeholder="Duration"
                                  />
                                  {errors.pricing?.[index]?.durationType && (
                                    <p className="text-danger small mb-0">
                                      {
                                        errors.pricing[index].durationType
                                          .message
                                      }
                                    </p>
                                  )}
                                </Col> 
                                <Col md={2}>
                                  <Form.Select
                                    className="scrollable-select"
                                    {...register(
                                      `pricing.${index}.durationType`,
                                      {
                                        required: t(
                                          "validation.durationTypeRequired"
                                        ),
                                      }
                                    )}
                                  >
                                    <option value="">Select Duration</option>
                                    {[
                                      "2hour",
                                      "3hour",
                                      "4hour",
                                      "5hour",
                                      "6hour",
                                      "12hour",
                                      "overnight",
                                      "1d",
                                      "2d",
                                      "3d",
                                      "7d",
                                      "15d",
                                      "30d",
                                    ].map((d) => (
                                      <option key={d} value={d}>
                                        {d
                                          .replace(/(\d+)([a-z]+)/, "$1 $2")
                                          .toUpperCase()}
                                      </option>
                                    ))}
                                  </Form.Select>
                                </Col>
                                <Col md={2}>
                                  <input
                                    type="number"
                                    className="form-control"
                                    {...register(`pricing.${index}.basePrice`, {
                                      valueAsNumber: true,
                                      min: {
                                        value: 0,
                                        message: t(
                                          "validation.basePriceNegative"
                                        ),
                                      },
                                    })}
                                    min={0}
                                    placeholder="Week Rate"
                                  />
                                  {errors.pricing?.[index]?.basePrice && (
                                    <p className="text-danger small mb-0">
                                      {errors.pricing[index].basePrice.message}
                                    </p>
                                  )}
                                </Col>

                                <Col md={2}>
                                  <input
                                    type="number"
                                    className="form-control"
                                    {...register(`pricing.${index}.weekRate`, {
                                      valueAsNumber: true,
                                      min: {
                                        value: 0,
                                        message: t(
                                          "validation.discountNegative"
                                        ),
                                      },
                                    })}
                                    min={0}
                                    placeholder="Weekend Rate"
                                  />
                                  {errors.pricing?.[index]?.weekRate && (
                                    <p className="text-danger small mb-0">
                                      {errors.pricing[index].weekRate.message}
                                    </p>
                                  )}
                                </Col>

                                <Col md={2}>
                                  <input
                                    type="number"
                                    className="form-control"
                                    {...register(`pricing.${index}.discount`, {
                                      valueAsNumber: true,
                                      min: {
                                        value: 0,
                                        message: t(
                                          "validation.discountNegative"
                                        ),
                                      },
                                      max: {
                                        value: 100,
                                        message: t("validation.discountExceed"),
                                      },
                                    })}
                                    min={0}
                                    max={100}
                                    placeholder="Discount %"
                                  />
                                  {errors.pricing?.[index]?.discount && (
                                    <p className="text-danger small mb-0">
                                      {errors.pricing[index].discount.message}
                                    </p>
                                  )}
                                </Col>

                                <Col
                                  md={3}
                                  className="checkboxlebels d-flex align-items-center gap-3 py-2 justify-content-start"
                                >
                                  <FormGroup check>
                                    <Label check>
                                      <Input
                                        type="checkbox"
                                        className="chekboxCustom"
                                        {...register(
                                          `pricing.${index}.activated`
                                        )}
                                      />{" "}
                                      {t("manageRoom.activated", "Activated")}
                                    </Label>
                                  </FormGroup>

                                  <FormGroup check>
                                    <Label check>
                                      <Input
                                        type="checkbox"
                                        className="chekboxCustom"
                                        {...register(
                                          `pricing.${index}.refundable`
                                        )}
                                      />{" "}
                                      {t("manageRoom.refundable")}
                                    </Label>
                                  </FormGroup>
                                </Col>

                                <Col md={1} className="p-0">
                                  <Button
                                    variant="danger"
                                    onClick={() => remove(index)}
                                  >
                                    {t("common.remove")}
                                  </Button>
                                </Col>
                              </Row>
                            ))}

                            <Button
                              variant="primary"
                              onClick={() =>
                                append({
                                  durationType: "",
                                  basePrice: 0,
                                  weekRate: 0,
                                  discount: 0,
                                  activated: true,
                                  refundable: true,
                                })
                              }
                            >
                              {t("common.addRate")}
                            </Button>
                          </Col>
                        </Row>
                      </div>
                    </Col>*/}

                    <Col lg="12" className="my-2">
                      <div className="d-flex align-items-center justify-content-center gap-10">
                        <Button
                          type="button"
                          onClick={handleCancel}
                          className="commonBtn GreyBtn"
                        >
                          {t("common.cancel")}
                        </Button>
                        <Button type="submit" className="commonBtn">
                          {loader ? "Submitting..." : t("common.submit")}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Form>
              </div>
            </Col>
          </Row>
        </Container>
      </section >
    </>
  );
};

export default AddRoom;
